<template>
  <section id="services" class="service-section section-padding">
    <div class="container">
      <!-- 大白色圆角容器 -->
      <div class="service-module-container">
        <div class="text-center mb-4">
          <h2 class="section-title">服务项目</h2>
          <div class="section-divider w-20 mx-auto"></div>
        </div>

        <!-- 服务分类标签 -->
        <div class="flex justify-center mb-8">
          <div class="inline-flex bg-white rounded-lg shadow-sm p-1">
            <div
              v-for="(tag, index) in serviceTags"
              :key="index"
              class="service-tab px-6 py-2 rounded-md text-sm font-medium transition-all"
              :class="{ active: activeTag === index }"
              @click="activeTag = index"
            >
              {{ tag }}
            </div>
          </div>
        </div>

        <!-- 服务卡片展示 -->
        <div class="service-grid grid grid-cols-2 md:service-grid-responsive gap-3 mb-8">
          <div v-if="loading" class="loading-text col-span-full">加载中...</div>
          <div v-else-if="!services[activeTag] || services[activeTag].length === 0" class="empty-text col-span-full">暂无数据</div>
          <div
            v-else
            v-for="(service, index) in services[activeTag]"
            :key="index"
            class="service-item"
            :class="{ 'service-item-hidden': !gridReady }"
            @click="goToServiceDetail(service.id)"
          >
            <div class="card-base p-4 h-full flex flex-col group cursor-pointer fade-in-up">
              <div class="service-item-title-wrap">
                <h4 class="service-item-title text-sm font-semibold mb-2 transition-colors duration-300">
                  {{ service.title }}
                </h4>
              </div>
              <div class="service-item-desc-wrap">
                <p class="service-item-desc text-xs">
                  {{ service.description }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 查看更多按钮 -->
        <div class="text-center">
          <button class="btn-primary" @click="goToServicesPage">查看更多 >></button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { api } from '../api'

export default {
  name: 'ServiceSection',
  data() {
    return {
      activeTag: 0,
      serviceTags: [],
      categories: [],
      services: {},
      loading: false,
      gridReady: false
    }
  },
  async mounted() {
    await this.loadCategories()
    window.addEventListener('resize', this.equalizeTitleHeights)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.equalizeTitleHeights)
  },
  watch: {
    activeTag() {
      this.gridReady = false
      this.loadServiceItems()
    }
  },
  methods: {
    async loadCategories() {
      try {
        this.loading = true
        const categories = await api.getCategories()
        const serviceCategories = categories.filter(cat => cat.firstLevelModule === '服务项目')
        this.categories = serviceCategories
        this.serviceTags = serviceCategories.map(cat => cat.secondLevelModule)
        await this.loadServiceItems()
      } catch (error) {
        console.error('加载分类失败:', error)
      } finally {
        this.loading = false
      }
    },
    async loadServiceItems() {
      if (this.categories.length === 0 || this.activeTag >= this.categories.length) return

      try {
        const categoryId = this.categories[this.activeTag].id
        const result = await api.getArticles(categoryId)
        this.services[this.activeTag] = result.rows.map(article => ({
          id: article.id,
          title: article.title || '暂无标题',
          description: article.summary || article.content?.substring(0, 100) + '...' || '暂无描述',
          publishedTime: article.publishedTime
        }))
        await this.$nextTick()
        this.equalizeTitleHeights()
      } catch (error) {
        console.error('加载服务列表失败:', error)
        this.services[this.activeTag] = []
      }
    },
    goToServiceDetail(id) {
      this.$router.push(`/article/${id}`)
    },
    goToServicesPage() {
      this.$router.push({ path: '/services', query: { category: this.serviceTags[this.activeTag] } })
    },
    equalizeTitleHeights() {
      const grid = this.$el.querySelector('.service-grid')
      if (!grid) return
      const titles = grid.querySelectorAll('.service-item-title')
      titles.forEach(t => t.style.minHeight = '')

      const rows = []
      let currentRow = []
      let lastTop = -1

      titles.forEach(title => {
        const top = title.getBoundingClientRect().top
        if (lastTop >= 0 && Math.abs(top - lastTop) > 2) {
          rows.push(currentRow)
          currentRow = []
        }
        lastTop = top
        currentRow.push(title)
      })
      if (currentRow.length > 0) rows.push(currentRow)

      rows.forEach(row => {
        const maxHeight = Math.max(...row.map(t => t.scrollHeight))
        row.forEach(t => { t.style.minHeight = maxHeight + 'px' })
      })
      this.gridReady = true
    }
  }
}
</script>

<style scoped>
.service-section {
  background-color: var(--bg);
  position: relative;
  overflow: hidden;
  padding: 16px 0;
}

/* 大白色圆角容器 */
.service-module-container {
  background: #ffffff;
  border-radius: 32px;
  box-shadow: 0 20px 40px -15px rgba(37,99,235,0.08);
  padding: 48px 40px;
  margin-bottom: 0;
  border: 1px solid rgba(37,99,235,0.12);
}

.service-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 50%, rgba(96, 165, 250, 0.1) 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
}

/* 标签样式 */
.service-tab {
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  color: var(--text-secondary);
  border: 1px solid var(--border);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.service-tab:hover {
  background: rgba(96, 165, 250, 0.1);
  border-color: rgba(96, 165, 250, 0.3);
  color: var(--accent);
}

.service-tab.active {
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  color: white;
  box-shadow: 0 4px 16px rgba(96, 165, 250, 0.4);
  border-color: var(--accent);
}

/* 服务网格 */
.service-grid {
  display: grid;
  gap: 16px;
  position: relative;
  z-index: 1;
}

@media (min-width: 768px) {
  .md\:service-grid-responsive {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px;
  }
}

/* 服务项样式 */
.service-item {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.service-item-hidden {
  opacity: 0;
  pointer-events: none;
}

.card-base {
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-2xl);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.card-base:hover {
  transform: translateY(-8px) scale(1.015);
  box-shadow: 0 15px 35px -10px rgba(37, 99, 235, 0.15), 0 0 20px rgba(96, 165, 250, 0.2);
  border-color: rgba(96, 165, 250, 0.3);
}

.service-item-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
  margin-bottom: 0.75rem;
  position: relative;
  z-index: 1;
}

.service-item:hover .service-item-title {
  color: var(--accent);
  text-shadow: 0 0 10px rgba(96, 165, 250, 0.5);
}

.service-item-desc {
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.4;
  position: relative;
  z-index: 1;
}

.service-item-title-wrap {
  min-height: 3rem;
  text-align: left;
}

.service-item-desc-wrap {
  min-height: 2.5rem;
  text-align: left;
}

/* 渐入动画 */
.fade-in-up {
  animation: fadeInUp 0.6s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 状态样式 */
.loading-text,
.empty-text {
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
  font-size: 14px;
  background: var(--surface);
  backdrop-filter: blur(20px) saturate(120%);
  -webkit-backdrop-filter: blur(20px) saturate(120%);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  border: 1px solid var(--border);
  position: relative;
  z-index: 1;
}

/* 按钮样式 */
.btn-primary {
  padding: 12px 32px;
  border: none;
  border-radius: 50px;
  background: linear-gradient(135deg, var(--accent), var(--accent-light));
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
  position: relative;
  overflow: hidden;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  animation: breathe 2s ease-in-out infinite;
}

@keyframes breathe {
  0%, 100% {
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
  }
  50% {
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.6);
  }
}

/* 响应式设计 */
@media (max-width: 767px) {
  .md\:service-grid-responsive {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .md\:service-grid-responsive {
    grid-template-columns: 1fr;
  }

  .service-tab {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }
}
</style>
